/**
 * Google Identity Services button for the public pages (join by link or code).
 *
 * The same idea as the panel's GoogleSignInButton, kept self-contained because
 * client-view has no query library and never holds a session. Renders nothing
 * unless the API reports a web client id, so an unconfigured deployment shows
 * exactly the old page. Hands back a Google ID token; nothing else.
 */
import React, { useEffect, useRef, useState } from 'react';

import { getGoogleWebClientId } from './join-api';

const GSI_SRC = 'https://accounts.google.com/gsi/client';

interface GoogleAccountsId {
  initialize(config: {
    client_id: string;
    callback: (response: { credential?: string }) => void;
    ux_mode?: 'popup';
    auto_select?: boolean;
  }): void;
  renderButton(
    parent: HTMLElement,
    options: {
      theme?: 'outline';
      size?: 'large';
      text?: 'continue_with' | 'signup_with';
      shape?: 'pill';
      width?: number;
    },
  ): void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleAccountsId } };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadGoogleScript(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  scriptPromise ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Could not load Google sign-in'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export function GoogleButton({
  onCredential,
}: {
  onCredential: (idToken: string) => void;
}): JSX.Element | null {
  const [clientId, setClientId] = useState<string | null>(null);
  const host = useRef<HTMLDivElement>(null);
  const callback = useRef(onCredential);
  callback.current = onCredential;

  useEffect(() => {
    let cancelled = false;
    void getGoogleWebClientId().then((id) => {
      if (!cancelled) setClientId(id);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (clientId === null) return undefined;
    let cancelled = false;
    loadGoogleScript()
      .then(() => {
        const api = window.google?.accounts?.id;
        if (cancelled || !api || !host.current) return;
        api.initialize({
          client_id: clientId,
          callback: (response) => {
            if (response.credential) callback.current(response.credential);
          },
          ux_mode: 'popup',
          auto_select: false,
        });
        host.current.replaceChildren();
        api.renderButton(host.current, {
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'pill',
          width: Math.min(host.current.clientWidth || 320, 400),
        });
      })
      .catch(() => {
        // Google unreachable: the password form below still works.
        if (!cancelled) setClientId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  if (clientId === null) return null;
  return (
    <div className="join-google">
      <div ref={host} className="join-google__button" data-testid="join-google" />
      <p className="join-google__or" aria-hidden="true">
        <span>or</span>
      </p>
    </div>
  );
}
